import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User, AuthState, LoginCredentials, AuthResponse } from '@/types';
import { authApi } from '@/lib/api';
import { useSessionStore } from '@/store/sessionStore';

interface AuthActions {
    login: (credentials: LoginCredentials) => Promise<void>;
    labLogin: (payload: { institute_code: string; email: string; password: string }) => Promise<void>;
    superAdminLogin: (password: string) => Promise<void>;
    logout: () => void;
    refreshToken: () => Promise<void>;
    setUser: (user: User) => void;
    clearError: () => void;
}

interface AuthStore extends AuthState, AuthActions {
    error: string | null;
    // True when the student signed in through the browser lab login
    // (learn.eswift.in/lab/login). Lets ProtectedRoute admit them to the web
    // student portal, which otherwise bounces every student to the exam client.
    labSession: boolean;
}

export const useAuthStore = create<AuthStore>()(
    persist(
        (set, get) => ({
            // Initial state
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            labSession: false,

            // Actions
            login: async (credentials: LoginCredentials) => {
                set({ isLoading: true, error: null });

                try {
                    const response = await authApi.login(credentials);
                    const { user, token } = response;

                    set({
                        user,
                        token,
                        isAuthenticated: true,
                        isLoading: false,
                        error: null,
                        labSession: false,
                    });
                } catch (error: any) {
                    set({
                        isLoading: false,
                        error: error.message || 'Login failed',
                    });
                    throw error;
                }
            },

            labLogin: async (payload: { institute_code: string; email: string; password: string }) => {
                set({ isLoading: true, error: null });
                try {
                    const response = await authApi.labLogin(payload);
                    set({
                        user: response.user,
                        token: response.token,
                        isAuthenticated: true,
                        isLoading: false,
                        error: null,
                        labSession: true,
                    });
                } catch (error: any) {
                    set({
                        isLoading: false,
                        error: error?.response?.data?.error?.message || error.message || 'Login failed',
                    });
                    throw error;
                }
            },

            superAdminLogin: async (password: string) => {
                set({ isLoading: true, error: null });

                try {
                    const response = await authApi.superAdminLogin(password);
                    const { user, token } = response;

                    set({
                        user,
                        token,
                        isAuthenticated: true,
                        isLoading: false,
                        error: null,
                    });
                } catch (error: any) {
                    set({
                        isLoading: false,
                        error: error.error?.message || error.message || 'Login failed',
                    });
                    throw error;
                }
            },

            logout: () => {
                set({
                    user: null,
                    token: null,
                    isAuthenticated: false,
                    error: null,
                    labSession: false,
                });
                // Drop the admission-session scope so the next user doesn't
                // inherit the previous institute's selected session.
                useSessionStore.getState().reset();
                // Clear any session data
                sessionStorage.clear();
            },

            refreshToken: async () => {
                const { token } = get();
                if (!token) return;

                try {
                    const response = await authApi.refresh(token);
                    set({
                        token: response.token,
                        user: response.user,
                    });
                } catch {
                    // Token refresh failed, logout
                    get().logout();
                }
            },

            setUser: (user: User) => {
                set({ user });
            },

            clearError: () => {
                set({ error: null });
            },
        }),
        {
            name: 'auth-storage',
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({
                user: state.user,
                token: state.token,
                isAuthenticated: state.isAuthenticated,
                labSession: state.labSession,
            }),
        }
    )
);
